import { useEffect, useId, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { CampoControlado, Entrada, EntradaDinheiro, focarPrimeiroInvalido, Rotulo, Selecao, useCamposPersonalizados, validarCampos } from '../../componentes/formulario';
import { nomeDaEmpresa, type EmpresaCatalogo } from '../../componentes/verticais';
import { api, getTodos, mensagemDeErro, patch, post } from '../../nucleo/api';
import { numero } from '../../nucleo/formato';
import { useSessao } from '../../nucleo/sessao';

/**
 * Lista de 03/11, itens 8 e 9: o formulário do catálogo de venda (`cadastros.servico`), um só para o botão "Criar novo
 * produto/serviço" da negociação e para a página Produtos e Serviços das Configurações do CRM. Nome, preço-base, a vertical do
 * cadastro (qualquer uma das que a pessoa vê, ou "Todas") e os campos personalizados do catálogo (`comCampos`, nos dois lugares).
 */

/** A opção "Todas" da vertical do cadastro (item 15 de 30/10). */
export const TODAS = 'todas';
/** Edição de um cadastro com mais de uma vertical: manter como está (o formulário só escolhe uma ou "Todas"). */
export const VARIAS = 'varias';

export interface ServicoCatalogo {
  id: string;
  nome: string;
  codigo: string | null;
  preco_venda_centavos: number;
  tipo_cobranca: string | null;
  /** V1/V3: verticais em que o serviço é oferecido (vazio ou ausente = "Todas"). */
  empresas?: string[];
  /** rl0311-4 ROB4-B1: a moeda do cadastro (criado na negociação, é a dela). */
  moeda?: string;
  extras?: Record<string, unknown>;
}

/** O catálogo de venda do Cadastros (uma consulta só, dividida entre o formulário, a lista da negociação e a página das Configurações). */
export function useCatalogoVenda() {
  const s = useSessao();
  // Review rcrm3 robustez (B5): o catálogo é do Cadastros; sem ele instalado ou lido, a lista nem é pedida.
  const podeCatalogo = s.instalado('cadastros') && s.pode('cadastros.ver');
  const q = useQuery({ queryKey: ['cadastros', 'servicos', 'venda'], queryFn: () => getTodos<ServicoCatalogo>('/cadastros/servicos', { uso: 'venda', ativo: true }).then((r) => r.itens), enabled: podeCatalogo });
  return Object.assign(q, { podeCatalogo });
}

export interface DadosServico {
  nome: string;
  centavos: number;
  /** O id da vertical, `TODAS`, `VARIAS` (edição sem mexer) ou vazio (sem vertical a perguntar). */
  vertical: string;
  extras: Record<string, unknown>;
}

/**
 * Na criação, só os preenchidos. Na edição, o campo limpo vai vazio (rl0311-1 ROB-M1): o servidor junta com o que já havia, e só
 * a chave vazia apaga o valor antigo.
 */
export const extrasDoEnvio = (valores: Record<string, unknown>, editando: boolean): Record<string, unknown> =>
  Object.fromEntries(Object.entries(valores).flatMap(([k, v]) => (v !== '' && v !== undefined && v !== null ? [[k, v]] : editando ? [[k, '']] : [])));

/** O valor inicial do seletor para um cadastro que já existe. */
export const verticalDoCadastro = (x: Pick<ServicoCatalogo, 'empresas'>) => (!x.empresas?.length ? TODAS : x.empresas.length === 1 ? x.empresas[0]! : VARIAS);

/** Grava no catálogo (criar e editar), com a mesma entrada da API; a escrita invalida `['cadastros','servicos']`. */
export function useGravarServico(moeda: string) {
  const qc = useQueryClient();
  return {
    criar: async (d: DadosServico): Promise<ServicoCatalogo> => {
      const id = crypto.randomUUID();
      // Val1 e item 15 de 30/10: o cadastro nasce na vertical dada (sem vertical, no escopo de quem cria).
      const verticais = d.vertical === TODAS ? { todas: true, empresas: [] } : d.vertical && d.vertical !== VARIAS ? { todas: false, empresas: [d.vertical] } : null;
      await post('/cadastros/servicos', { id, nome: d.nome, preco_venda_centavos: d.centavos, uso: 'venda', moeda, ...(verticais ? { verticais } : {}), ...(Object.keys(d.extras).length ? { extras: d.extras } : {}) });
      await qc.invalidateQueries({ queryKey: ['cadastros', 'servicos'] });
      return { id, nome: d.nome, codigo: null, preco_venda_centavos: d.centavos, tipo_cobranca: null, empresas: verticais && !verticais.todas ? verticais.empresas : [] };
    },
    editar: async (atual: ServicoCatalogo, d: DadosServico): Promise<void> => {
      // rl0311-4 VIS4-B4: se a troca de vertical falhar depois do PATCH, a lista mostra o que já foi gravado.
      try {
      await patch(`/cadastros/servicos/${atual.id}`, { nome: d.nome, preco_venda_centavos: d.centavos, ...(Object.keys(d.extras).length || Object.keys(atual.extras ?? {}).length ? { extras: { ...(atual.extras ?? {}), ...d.extras } } : {}) });
      // A vertical muda pela marcação do núcleo (a mesma regra de quem marca); o PATCH do serviço não a recebe.
      if (d.vertical && d.vertical !== VARIAS && d.vertical !== verticalDoCadastro(atual)) {
        await api('PUT', `/verticais/cadastros.servico/${atual.id}`, d.vertical === TODAS ? { todas: true, empresas: [] } : { todas: false, empresas: [d.vertical] });
      }
      } finally {
        await qc.invalidateQueries({ queryKey: ['cadastros', 'servicos'] });
      }
    },
  };
}

export function FormServicoCatalogo({
  visiveis,
  verticalInicial = '',
  atual = null,
  comCampos = false,
  confirmarDesabilitado = false,
  ajudaVertical,
  rotuloConfirmar,
  varianteConfirmar = 'secundario',
  aoCancelar,
  aoGravar,
  aoGravado,
}: {
  /** Todas as verticais que a pessoa vê (a ficha mostra o seletor só com mais de uma). */
  visiveis: readonly EmpresaCatalogo[];
  /** A vertical do item da negociação, quando há: o seletor já vem nela. */
  verticalInicial?: string;
  atual?: ServicoCatalogo | null;
  comCampos?: boolean;
  confirmarDesabilitado?: boolean;
  /** rl0311-4 VIS4-B2: a ajuda do seletor, que só o card tem (lá a vertical pode ficar fora do filtro). */
  ajudaVertical?: string;
  rotuloConfirmar: string;
  varianteConfirmar?: 'primario' | 'secundario';
  aoCancelar: () => void;
  /** Grava (criar ou editar); recebe os dados já validados. Devolve o que o chamador precisa depois. */
  aoGravar: (d: DadosServico) => Promise<unknown>;
  aoGravado?: (resultado: unknown, d: DadosServico) => void;
}) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const id = useId();
  const [nome, setNome] = useState(atual?.nome ?? '');
  // rl0311-3 VIS3-M2: o preço pelo campo de dinheiro do sistema (máscara e modo discreto iguais aos outros).
  const [centavos, setCentavos] = useState<number | null>(atual ? atual.preco_venda_centavos : null);
  const perguntaVertical = visiveis.length > 1;
  const [vertical, setVertical] = useState(atual ? verticalDoCadastro(atual) : verticalInicial);
  // rl0311-5 SPEC5-B1: aberto antes de as verticais voltarem, o seletor acompanha a do item até a pessoa escolher.
  const tocouVertical = useRef(false);
  useEffect(() => {
    if (!atual && !tocouVertical.current && verticalInicial) {
      setVertical(verticalInicial);
      setErroVertical(false);
    }
  }, [atual, verticalInicial]);
  const [extras, setExtras] = useState<Record<string, unknown>>(atual?.extras ?? {});
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [gravando, setGravando] = useState(false);
  const defs = useCamposPersonalizados(comCampos ? 'cadastros.servico' : undefined);
  const opcoes = [
    ...visiveis.map((e) => ({ valor: e.id, rotulo: nomeDaEmpresa(e) })),
    { valor: TODAS, rotulo: t('crm.ficha.produtos.verticalTodas') },
    ...(vertical === VARIAS || (atual && verticalDoCadastro(atual) === VARIAS) ? [{ valor: VARIAS, rotulo: t('crm.cfg.produtos.verticalVarias') }] : []),
  ];
  const faltaVertical = perguntaVertical && !vertical;
  // rl0311-1 VIS-M3: Enter repetido não cria outro cadastro enquanto o primeiro grava (o estado só muda no render seguinte).
  const emEnvio = useRef(false);
  const raiz = useRef<HTMLDivElement>(null);
  const [foco, setFoco] = useState<{ n: number; nomes: string[] }>({ n: 0, nomes: [] });
  const [erroVertical, setErroVertical] = useState(false);
  const alerta = useRef<HTMLParagraphElement>(null);
  // rl0311-4 VIS4-M1: o erro do servidor fica à vista, como no `Formulario`.
  useEffect(() => {
    if (erro) alerta.current?.focus();
  }, [erro]);
  useEffect(() => {
    if (foco.n && raiz.current) focarPrimeiroInvalido(raiz.current, foco.nomes);
  }, [foco]);
  const envia = async () => {
    if (emEnvio.current) return;
    setErro(null);
    const locais = validarCampos(defs, { extras });
    setErros(locais);
    // rl0311-3 VIS3-M1: o erro aparece à vista: o foco vai ao primeiro campo inválido, como no `Formulario`.
    // rl0311-4 VIS4-B1: a vertical em branco é campo inválido como os outros.
    setErroVertical(faltaVertical);
    if (faltaVertical || Object.keys(locais).length) setFoco((x) => ({ n: x.n + 1, nomes: [...(faltaVertical ? ['vertical'] : []), ...Object.keys(locais)] }));
    if (!nome.trim() || faltaVertical || Object.keys(locais).length) return;
    const dados: DadosServico = { nome: nome.trim(), centavos: centavos && centavos > 0 ? centavos : 0, vertical: perguntaVertical ? vertical : '', extras: extrasDoEnvio(extras, !!atual) };
    emEnvio.current = true;
    setGravando(true);
    try {
      aoGravado?.(await aoGravar(dados), dados);
    } catch (e) {
      setErro(mensagemDeErro(e));
    } finally {
      emEnvio.current = false;
      setGravando(false);
    }
  };
  return (
    <div
      ref={raiz}
      className="flex min-w-0 flex-col gap-2 rounded-lg border border-borda bg-sup-2 p-3"
      data-form-novo-produto=""
      data-form-servico-catalogo=""
      onKeyDown={(e) => {
        // Enter no nome confirma, como num formulário; o botão segue sendo o caminho do teclado para o resto.
        if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT' && !confirmarDesabilitado && nome.trim()) {
          e.preventDefault();
          void envia();
        }
      }}
    >
      <Rotulo id={`${id}-nome`} rotulo={t('crm.ficha2.nomeProduto')} obrigatorio>
        <Entrada id={`${id}-nome`} maxLength={200} value={nome} onChange={(e) => setNome(e.target.value)} autoFocus data-nome-servico="" />
      </Rotulo>
      <Rotulo id={`${id}-preco`} rotulo={t('crm.ficha2.precoBase')}>
        <EntradaDinheiro id={`${id}-preco`} valor={centavos} aoMudar={setCentavos} placeholder={numero(0, 2)} data-preco-servico="" />
      </Rotulo>
      {perguntaVertical && (
        <div data-campo="vertical">
          <Rotulo id={`${id}-vertical-novo`} rotulo={t('crm.ficha.produtos.verticalDoProduto')} ajuda={ajudaVertical} erro={erroVertical ? t('crm.ficha.produtos.escolhaVertical') : undefined} obrigatorio>
            <Selecao id={`${id}-vertical-novo`} data-vertical-novo-produto="" value={vertical} vazio={t('crm.ficha.produtos.escolhaVertical')} opcoes={opcoes} aria-invalid={erroVertical || undefined} aria-describedby={erroVertical ? `${id}-vertical-novo-erro` : undefined} onChange={(e) => {
                tocouVertical.current = true;
                setVertical(e.target.value);
                setErroVertical(false);
              }} />
          </Rotulo>
        </div>
      )}
      {comCampos && defs.length > 0 && (
        <fieldset className="flex min-w-0 flex-col gap-2" data-campos-servico="">
          <legend className="mb-1 text-sm font-medium text-tinta-2">{t('crm.cfg.produtos.campos')}</legend>
          {defs
            .filter((d) => !d.visivel || d.visivel({ extras }))
            .map((d) => (
              <div key={d.nome} data-campo={d.nome}>
                <CampoControlado def={d} valor={extras[d.nome.slice(7)]} erro={erros[d.nome]} aoMudar={(v) => setExtras((x) => ({ ...x, [d.nome.slice(7)]: v }))} />
              </div>
            ))}
        </fieldset>
      )}
      {erro && (
        <p ref={alerta} tabIndex={-1} className="rounded-lg text-sm text-perigo focus:outline-2 focus:outline-foco" role="alert">
          {erro}
        </p>
      )}
      <div className="flex justify-end gap-2">
        <Botao tamanho="sm" variante="fantasma" onClick={aoCancelar}>
          {tn('comum.cancelar')}
        </Botao>
        {/* Na negociação o primário é o do rodapé ("Avançar"); aqui é secundário. Na página das Configurações, o do formulário aberto. */}
        <Botao tamanho="sm" variante={varianteConfirmar} disabled={!nome.trim() || faltaVertical || confirmarDesabilitado} carregando={gravando} onClick={() => void envia()} data-confirmar-novo-produto="">
          {rotuloConfirmar}
        </Botao>
      </div>
    </div>
  );
}
